/**
 * Shared helpers for working with image records returned by GET /images.
 * Used by Image Browser, Card Editor, and (eventually) Image Uploader.
 *
 * @typedef {Object} ImageRecord
 * @property {number} id
 * @property {number} category_id
 * @property {number} license_id
 * @property {number} image_type_id
 * @property {string|null} label_en
 * @property {string|null} label_de
 * @property {string|null} label_fr
 * @property {string|null} label_it
 * @property {string|null} alt_text_en
 * @property {string|null} alt_text_de
 * @property {string|null} alt_text_fr
 * @property {string|null} alt_text_it
 * @property {string} gcs_object_path
 * @property {string} mime_type
 * @property {number|null} file_size_bytes
 * @property {number|null} width_px
 * @property {number|null} height_px
 * @property {'DRAFT'|'PENDING'|'APPROVED'|'REJECTED'|'ARCHIVED'} status
 * @property {string|null} curator_notes
 * @property {Object|null} metadata
 * @property {string} uploaded_by
 * @property {string} uploaded_at
 * @property {string} updated_at
 */

const SUPPORTED_LOCALES = ['en', 'de', 'fr', 'it'];

const PLACEHOLDER_PALETTE = [
  '#2980b9', '#27ae60', '#e67e22', '#e74c3c', '#8e44ad',
  '#16a085', '#7f5539', '#d35400', '#3498db', '#7f8c8d',
];

/**
 * Whether a record's gcs_object_path points at a seed-cohort placeholder
 * (a fictional path like "placeholder/foo.png"). The actual file does not
 * exist in GCS, so we serve an inline SVG instead of triggering a 404.
 *
 * @param {ImageRecord} record
 * @returns {boolean}
 */
export function isPlaceholderRecord(record) {
  return typeof record?.gcs_object_path === 'string'
    && record.gcs_object_path.startsWith('placeholder/');
}

/**
 * Build the full GCS URL for an image record. The bucket name comes from the
 * VUE_APP_GCS_BUCKET env var (set in frontend/.env and .env.production).
 *
 * For seed-cohort records (gcs_object_path starts with "placeholder/") we
 * return an inline SVG directly instead of a URL that would 404. This avoids
 * a wave of failed HTTP requests on every page load.
 *
 * @param {ImageRecord} record
 * @returns {string}
 */
export function imageUrl(record) {
  if (!record || !record.gcs_object_path) {
    return makePlaceholderSvg('no image');
  }
  if (isPlaceholderRecord(record)) {
    const label = pickLocaleString(record, 'label', 'en') || 'no label';
    return makePlaceholderSvg(label);
  }
  const bucket = process.env.VUE_APP_GCS_BUCKET;
  if (!bucket) {
    // Fail loudly in dev — env var should always be set.
    // eslint-disable-next-line no-console
    console.warn('VUE_APP_GCS_BUCKET is not set; image URLs will be broken.');
  }
  return `https://storage.googleapis.com/${bucket}/${record.gcs_object_path}`;
}

/**
 * Pick the best string for a record + field + locale, with fallback chain:
 *   1. requested locale → 2. en → 3. first non-null among de/fr/it.
 * Returns null if all four language variants are null/empty.
 *
 * @param {ImageRecord} record
 * @param {'label'|'alt_text'} field
 * @param {'en'|'de'|'fr'|'it'} locale
 * @returns {string|null}
 */
export function pickLocaleString(record, field, locale) {
  if (!record) return null;
  const order = [];
  // Active locale first (deduplicated against the rest of the chain).
  if (SUPPORTED_LOCALES.includes(locale)) order.push(locale);
  for (const l of ['en', 'de', 'fr', 'it']) {
    if (!order.includes(l)) order.push(l);
  }
  for (const l of order) {
    const value = record[`${field}_${l}`];
    if (value && typeof value === 'string' && value.trim().length > 0) {
      return value;
    }
  }
  return null;
}

/**
 * Resolve the alt-text for an <img>. Falls back to the label chain if no
 * alt-text is available. Returns empty string as last resort (per WCAG,
 * empty alt is correct for purely decorative images — better than misleading
 * non-empty alt).
 *
 * @param {ImageRecord} record
 * @param {'en'|'de'|'fr'|'it'} locale
 * @returns {string}
 */
export function altTextFor(record, locale) {
  return pickLocaleString(record, 'alt_text', locale)
    ?? pickLocaleString(record, 'label', locale)
    ?? '';
}

/**
 * Stable, fast non-cryptographic hash of a string.
 * Used to pick a deterministic placeholder color from the label.
 *
 * @param {string} s
 * @returns {number}
 */
export function hashCode(s) {
  let h = 0;
  if (!s) return h;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) - h) + s.charCodeAt(i);
    h |= 0; // force 32-bit int
  }
  return h;
}

/**
 * Escape a string for safe inclusion inside SVG text content.
 *
 * @param {string} s
 * @returns {string}
 */
export function escapeXml(s) {
  return String(s).replace(/[<>&"']/g, c => ({
    '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&apos;',
  }[c]));
}

/**
 * Build an inline SVG data URI with a colored rectangle and centered label.
 * Used as a placeholder when an image is missing or its gcs_object_path is
 * a seed-cohort placeholder/ path.
 *
 * @param {string} label
 * @returns {string} data:image/svg+xml URI
 */
export function makePlaceholderSvg(label) {
  const safeLabel = escapeXml(label || '');
  const color = PLACEHOLDER_PALETTE[Math.abs(hashCode(label || '')) % PLACEHOLDER_PALETTE.length];
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="300" height="300"><rect width="100%" height="100%" fill="${color}"/><text x="50%" y="50%" font-family="Montserrat, Helvetica, Arial, sans-serif" font-size="28" font-weight="700" fill="#ffffff" text-anchor="middle" dominant-baseline="middle">${safeLabel}</text></svg>`;
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

/**
 * <img @error> handler that swaps a broken image URL with an inline SVG
 * placeholder. Pass the record + active locale so the placeholder can show
 * the right label.
 *
 * Usage in template:
 *   <img :src="imageUrl(record)" :alt="altTextFor(record, locale)"
 *        @error="onImgError($event, record, locale)">
 *
 * @param {Event} event
 * @param {ImageRecord} record
 * @param {'en'|'de'|'fr'|'it'} locale
 */
export function onImgError(event, record, locale) {
  const img = event.target;
  if (!img || img.dataset.fallbackApplied === '1') return; // avoid loop
  img.dataset.fallbackApplied = '1';
  const label = pickLocaleString(record, 'label', locale) || 'no label';
  img.src = makePlaceholderSvg(label);
}
